import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Activity, Clock, Shield, Database, RefreshCw, Trash2, Unlock, AlertTriangle, Check, Zap } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api/superadmin/system';

function StatCard({ label, value, icon: Icon, color, sub }) {
  return (
    <div className="p-5 rounded-sm" style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)' }}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={14} style={{ color }} />
        <p className="text-[10px] uppercase tracking-widest" style={{ color: 'var(--lux-text-muted)' }}>{label}</p>
      </div>
      <p className="font-serif text-2xl font-light mb-0.5" style={{ color }}>{value}</p>
      {sub && <p className="text-[11px]" style={{ color: 'var(--lux-text-muted-3)' }}>{sub}</p>}
    </div>
  );
}

export function SystemTab({ storedKey }) {
  const [status, setStatus] = useState(null);
  const [demos, setDemos] = useState([]);
  const [crons, setCrons] = useState([]);
  const [security, setSecurity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');

  const headers = { 'X-Superadmin-Key': storedKey };

  const load = async () => {
    setLoading(true);
    try {
      const [sRes, dRes, cRes, secRes] = await Promise.all([
        axios.get(`${API}/status`, { headers }),
        axios.get(`${API}/demos`, { headers }),
        axios.get(`${API}/crons`, { headers }),
        axios.get(`${API}/security`, { headers }),
      ]);
      setStatus(sRes.data);
      setDemos(dRes.data.demos);
      setCrons(cRes.data.crons);
      setSecurity(secRes.data);
    } catch { toast.error('Errore caricamento sistema.'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const unblockIp = async (ip) => {
    try {
      await axios.delete(`${API}/security/unblock/${encodeURIComponent(ip)}`, { headers });
      toast.success(`IP ${ip} sbloccato.`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore.'); }
  };

  const clearRateLimits = async () => {
    try {
      await axios.delete(`${API}/security/rate-limits`, { headers });
      toast.success('Rate limits resettati.');
      load();
    } catch { toast.error('Errore.'); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-6 h-6 border-2 rounded-full animate-spin" style={{ borderColor: 'var(--lux-gold)', borderTopColor: 'transparent' }} />
    </div>
  );

  const tabs = ['overview', 'demo', 'cron', 'security'];
  const tabLabels = { overview: 'Panoramica', demo: 'Demo Attive', cron: 'Cron Jobs', security: 'Sicurezza' };

  return (
    <div data-testid="system-tab">
      {/* Sub-navigation */}
      <div className="flex items-center gap-1 mb-6 p-1 rounded-sm" style={{ background: 'var(--lux-surface-3)' }}>
        {tabs.map(t => (
          <button key={t} onClick={() => setActiveSection(t)}
            className="flex-1 py-2 text-xs font-medium rounded-sm transition-all"
            style={{ background: activeSection === t ? 'var(--lux-surface-1)' : 'transparent', color: activeSection === t ? 'var(--lux-gold)' : 'var(--lux-text-muted)' }}>
            {tabLabels[t]}
          </button>
        ))}
        <button onClick={load} className="p-2 rounded-sm" style={{ color: 'var(--lux-text-muted)' }}>
          <RefreshCw size={13} />
        </button>
      </div>

      {/* Overview */}
      {activeSection === 'overview' && status && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Uptime" value={status.uptime} icon={Activity} color="#059669" />
            <StatCard label="Demo Attive" value={status.stats.demo_active} icon={Clock} color="#c9a84c" />
            <StatCard label="Tenant Attivi" value={status.stats.tenants_active} icon={Database} color="#7c3aed" />
            <StatCard label="IP Bloccati" value={status.security.blocked_ips} icon={Shield} color={status.security.blocked_ips > 0 ? '#dc2626' : '#059669'} />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <StatCard label="Lead Totali" value={status.stats.leads_total} icon={Activity} color="#0891b2" />
            <StatCard label="Campagne Inviate" value={status.stats.campaigns_sent} icon={Zap} color="#c9a84c" />
            <StatCard label="Rate Limit Entries" value={status.security.rate_limit_entries} icon={Shield} color="#6b7280" />
          </div>
          {/* Drip sequence */}
          <div className="p-5 rounded-sm" style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)' }}>
            <p className="text-[10px] uppercase tracking-widest mb-3" style={{ color: 'var(--lux-gold)' }}>Sequenza Drip AI</p>
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full" style={{ background: status.drip_sequence.active ? '#059669' : '#555' }} />
                <span style={{ color: 'var(--lux-text)' }}>{status.drip_sequence.active ? 'Attiva' : 'Inattiva'}</span>
              </div>
              <span style={{ color: 'var(--lux-text-muted)' }}>Status: {status.drip_sequence.batch_status || '—'}</span>
              <span style={{ color: 'var(--lux-text-muted)' }}>Server: {status.server_time?.slice(0, 19).replace('T', ' ')} UTC</span>
            </div>
          </div>
        </div>
      )}

      {/* Demo Attive */}
      {activeSection === 'demo' && (
        <div>
          <p className="text-sm mb-4" style={{ color: 'var(--lux-text-muted)' }}>{demos.filter(d => !d.expired).length} demo attive</p>
          <div className="space-y-2">
            {demos.map((d) => (
              <div key={d.slug} className="flex items-center gap-4 px-5 py-3.5 rounded-sm"
                style={{ background: 'var(--lux-surface-1)', border: `1px solid ${d.expired ? 'var(--lux-border-1)' : 'rgba(201,168,76,0.2)'}` }}>
                <div className="w-2 h-2 rounded-full shrink-0" style={{ background: d.expired ? '#444' : '#059669' }} />
                <div className="flex-1">
                  <p className="text-sm font-medium" style={{ color: d.expired ? 'var(--lux-text-muted)' : 'var(--lux-text)' }}>{d.name}</p>
                  <p className="text-xs" style={{ color: 'var(--lux-text-muted-3)' }}>{d.owner_email} · {d.slug}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs font-medium" style={{ color: d.expired ? '#555' : '#c9a84c' }}>{d.remaining}</p>
                  <p className="text-[10px]" style={{ color: 'var(--lux-text-muted-3)' }}>Creata: {d.created_at?.slice(0, 10)}</p>
                </div>
              </div>
            ))}
            {demos.length === 0 && <p className="text-xs text-center py-8" style={{ color: 'var(--lux-text-muted-3)' }}>Nessuna demo richiesta.</p>}
          </div>
        </div>
      )}

      {/* Cron Jobs */}
      {activeSection === 'cron' && (
        <div className="space-y-3">
          {crons.map((c) => (
            <div key={c.job} className="p-5 rounded-sm" style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)' }}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="font-medium text-sm" style={{ color: 'var(--lux-text)' }}>{c.name}</p>
                  <p className="text-[10px]" style={{ color: 'var(--lux-text-muted)' }}>Schedule: {c.schedule}</p>
                </div>
                <span className="text-[10px] px-2 py-0.5 rounded-full"
                  style={{ background: c.status === 'active' ? 'rgba(5,150,105,0.1)' : 'rgba(100,100,100,0.1)', color: c.status === 'active' ? '#059669' : '#888' }}>
                  {c.status}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <p style={{ color: 'var(--lux-text-muted)' }}>Ultima esecuzione</p>
                  <p style={{ color: 'var(--lux-text)' }}>{c.last_run ? c.last_run.slice(0, 19).replace('T', ' ') : '—'}</p>
                </div>
                <div>
                  <p style={{ color: 'var(--lux-text-muted)' }}>Risultato</p>
                  <p style={{ color: 'var(--lux-text)' }}>{c.last_result}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Security */}
      {activeSection === 'security' && security && (
        <div className="space-y-5">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium" style={{ color: 'var(--lux-text)' }}>{security.blocked_ips?.length || 0} IP bloccati</p>
            <button onClick={clearRateLimits} className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-sm"
              style={{ border: '1px solid rgba(220,38,38,0.3)', color: '#dc2626' }}>
              <Trash2 size={11} /> Reset Rate Limits
            </button>
          </div>

          {/* Blocked IPs */}
          {security.blocked_ips?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-widest mb-2" style={{ color: 'var(--lux-text-muted)' }}>IP Bloccati</p>
              <div className="space-y-2">
                {security.blocked_ips.map((b, i) => (
                  <div key={i} className="flex items-center justify-between px-4 py-3 rounded-sm"
                    style={{ background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.2)' }}>
                    <div>
                      <code className="text-sm" style={{ color: '#dc2626' }}>{b.ip}</code>
                      <p className="text-xs mt-0.5" style={{ color: 'var(--lux-text-muted)' }}>{b.count} tentativi · {b.last_attempt?.slice(0, 16).replace('T', ' ')}</p>
                    </div>
                    <button onClick={() => unblockIp(b.ip)} className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-sm"
                      style={{ background: 'rgba(5,150,105,0.1)', color: '#059669', border: '1px solid rgba(5,150,105,0.3)' }}>
                      <Unlock size={10} /> Sblocca
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent attempts */}
          {security.recent_attempts?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-widest mb-2" style={{ color: 'var(--lux-text-muted)' }}>Tentativi recenti login</p>
              <div className="space-y-1.5">
                {security.recent_attempts.slice(0, 10).map((a, i) => (
                  <div key={i} className="flex items-center gap-3 px-4 py-2.5 rounded-sm text-xs"
                    style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)' }}>
                    <code style={{ color: 'var(--lux-text)' }}>{a.ip}</code>
                    <span style={{ color: a.count >= 5 ? '#dc2626' : '#d97706' }}>{a.count} tentativi</span>
                    <span style={{ color: 'var(--lux-text-muted-3)' }}>{a.last_attempt?.slice(0, 16).replace('T', ' ')}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <p className="text-xs" style={{ color: 'var(--lux-text-muted-3)' }}>
            Rate limit keys attivi: {security.rate_limit_active_keys}
          </p>
        </div>
      )}
    </div>
  );
}
